import { useState, useEffect } from "react";
import { useAuth, type AppRole } from "@/contexts/AuthContext";
import { useTourContext } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const ROLE_CHANGE_KEY = "nursly_last_known_role";

const roleLabels: Record<string, string> = {
  super_admin: "Owner",
  admin: "Admin",
  editor: "Editor",
  viewer: "Viewer",
};

/** Roles ordered by access level (lowest to highest) */
const roleOrder: AppRole[] = ["viewer", "editor", "admin", "super_admin"];

function getRoleIndex(role: AppRole): number {
  return roleOrder.indexOf(role);
}

/**
 * Returns the first tour step ID that belongs to the "new" capabilities
 * when upgrading from one role to another.
 */
function getDeltaStartStep(from: AppRole, to: AppRole): string | undefined {
  // For upgrades, we pick a logical starting step for the new capabilities
  const deltaMap: Record<string, string> = {
    "viewer->editor": "e-register",
    "viewer->admin": "a-dashboard",
    "viewer->super_admin": "a-dashboard",
    "editor->admin": "a-approvals",
    "editor->super_admin": "a-approvals",
    "admin->super_admin": "sa-access",
  };
  return deltaMap[`${from}->${to}`];
}

export function RoleChangeBanner() {
  const { role, profile } = useAuth();
  const [previousRole, setPreviousRole] = useState<AppRole | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  let tourContext: { startTour: (fromStep?: string) => void } | null = null;
  try {
    tourContext = useTourContext();
  } catch {
    // Not available yet
  }

  useEffect(() => {
    if (!role || !profile) return;

    try {
      const stored = localStorage.getItem(ROLE_CHANGE_KEY);
      if (stored) {
        const lastRole = stored as AppRole;
        if (lastRole !== role && getRoleIndex(role) > getRoleIndex(lastRole)) {
          // Role was upgraded
          setPreviousRole(lastRole);
          setShowBanner(true);
        }
      }
      // Always update stored role
      localStorage.setItem(ROLE_CHANGE_KEY, role);
    } catch {
      localStorage.setItem(ROLE_CHANGE_KEY, role);
    }
  }, [role, profile]);

  if (!showBanner || dismissed || !previousRole || !role) return null;

  const deltaStep = getDeltaStartStep(previousRole, role);

  return (
    <div className="flex items-center gap-3 px-4 py-3 mb-4 rounded-lg border bg-[hsl(var(--info)/0.08)] border-[hsl(var(--info)/0.2)]">
      <span className="text-lg">🔔</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          Your access level has been updated from {roleLabels[previousRole]} to {roleLabels[role]}.
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          There are new features available to you! Would you like a quick tour of what's changed?
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          onClick={() => {
            setDismissed(true);
            tourContext?.startTour(deltaStep);
          }}
        >
          Yes, show me →
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
